import { useEffect, useState } from 'react';

import { useDashboardStore } from '@/state/store';

/**
 * Top-of-page banner stack.
 *
 * Three slots driven by store events from the backend ambient layer:
 *
 *   * **Session warning** — ``building.session_warning`` fires
 *     5 min before market open / close.  Banner displays "Открытие
 *     через X мин" / "Закрытие через X мин"; cleared when the matching
 *     ``session_opened`` / ``session_closed`` event lands (the bell
 *     rings).
 *
 *   * **Factor crowd alert** — ``building.factor_crowd_detected`` raises
 *     a building-wide red banner with the offending pairwise correlation
 *     and which pods are affected.  Cleared by ``factor_crowd_relieved``.
 *
 *   * **Daily close ritual** — ``building.daily_close_started`` /
 *     ``..._completed`` paint a moonlit banner ("Daily close in
 *     progress" → "Day closed at 21:00 · pods=5 · net +12M ₽").  The
 *     completed banner auto-dismisses after ~30 s.
 *
 * All banners are pure DOM (no Pixi), sit absolutely positioned
 * directly under the header so they overlay the main grid without
 * shifting any other layout.
 */
export function SessionBanner() {
  const banners = useDashboardStore((s) => s.banners);
  const dailyClose = useDashboardStore((s) => s.atmosphere.dailyClose);
  const session = banners.session;
  const crowd = banners.factorCrowd;
  // Auto-dismiss the "completed" banner ~30 s after it lands so the
  // night ritual doesn't shadow the next trading day.  ``started``
  // banners stick around until the matching ``completed``.
  const [dismissCompleted, setDismissCompleted] = useState(false);
  useEffect(() => {
    if (dailyClose?.phase === 'completed') {
      setDismissCompleted(false);
      const id = window.setTimeout(() => setDismissCompleted(true), 30_000);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [dailyClose?.phase, dailyClose?.at]);
  const showDailyClose =
    dailyClose &&
    !(dailyClose.phase === 'completed' && dismissCompleted);
  if (!session && !crowd && !showDailyClose) return null;
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '6px 12px',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {crowd ? (
        <Banner
          accent="#f85149"
          icon="⚠"
          title="Factor crowd detected"
          detail={`max correlation ${crowd.maxCorrelation.toFixed(2)} > ${crowd.threshold.toFixed(2)} · ${crowd.affectedPods.length} pods`}
        />
      ) : null}
      {showDailyClose ? (
        <Banner
          accent={dailyClose.phase === 'started' ? '#7c3aed' : '#8b949e'}
          icon={dailyClose.phase === 'started' ? '☾' : '✓'}
          title={
            dailyClose.phase === 'started'
              ? 'Daily close in progress'
              : `Day closed at ${formatBoundaryTime(dailyClose.at)}`
          }
          detail={
            dailyClose.summary ??
            (dailyClose.phase === 'started'
              ? 'pods packing up · utility being recorded'
              : 'leaderboard refreshed')
          }
        />
      ) : null}
      {session ? (
        <Banner
          accent={session.boundaryKind === 'open' ? '#2ea043' : '#d29922'}
          icon={session.boundaryKind === 'open' ? '▲' : '▼'}
          title={
            session.boundaryKind === 'open'
              ? `Market opens in ${session.minutesUntil} min`
              : `Market closes in ${session.minutesUntil} min`
          }
          detail={`bell at ${formatBoundaryTime(session.boundaryAt)}`}
        />
      ) : null}
    </div>
  );
}

function Banner({
  accent,
  icon,
  title,
  detail,
}: {
  accent: string;
  icon: string;
  title: string;
  detail: string;
}) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 14px',
        background: `${accent}22`,
        border: `1px solid ${accent}`,
        borderRadius: 8,
        color: '#e6edf3',
        fontSize: 12,
        pointerEvents: 'auto',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <span
        aria-hidden
        style={{ color: accent, fontSize: 14, fontWeight: 700, lineHeight: 1 }}
      >
        {icon}
      </span>
      <span style={{ fontWeight: 600 }}>{title}</span>
      <span style={{ color: '#8b949e' }}>·</span>
      <span style={{ color: '#8b949e' }}>{detail}</span>
    </div>
  );
}

function formatBoundaryTime(iso: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
